'use client'

import { useAtom } from 'jotai'
import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import { SegmentedControl } from '@/components/ui/segmented-control'
import {
  FormInput,
  FormSection,
  FormToggleRow,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { RfqAutoFields } from '@/features/rfq/components/rfq-auto-fields'
import { RfqInstrumentSelect } from '@/features/rfq/components/rfq-instrument-select'
import { RfqMakerPicker } from '@/features/rfq/components/rfq-maker-picker'
import { resolveSingleRfqAccountId } from '@/features/rfq/lib/build-rfq-params'
import { composeRfqDraft } from '@/features/rfq/lib/rfq-draft'
import { validateRfqDraft } from '@/features/rfq/lib/rfq-validation'
import { RfqExecutionMode, RfqFormParams } from '@/features/rfq/types'
import { useCardAccountContext } from '@/hooks/use-card-account'
import { useNumberInput } from '@/hooks/use-number-format'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useInstrumentList } from '@/service/instrument.hook'
import { rfqFormParamsAtomFamily } from '@/state/rfq'
import { OrderSide } from '@/types/generated/common/trading'

const EXECUTION_MODES: RfqExecutionMode[] = ['manual', 'auto']

type HedgeSide = 'buy' | 'sell'

/**
 * The RFQ-only controls (makers, disclosure, manual/auto, optional fixed-price hedge)
 * that sit below the order-form legs when the form is in RFQ mode. The legs themselves
 * come from the shared order-form rows; these params live in `rfqFormParamsAtomFamily`.
 * Built from the shared order-form-fields kit, like every other order type's params.
 */
export function RfqParams({ cardId }: { cardId: string }) {
  const t = useTranslations('orderForm')
  const { tradeVenue: cardTradeVenue } = useCardAccountContext(cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const { data: instrumentListData } = useInstrumentList(primaryExchange)
  const [params, setParams] = useAtom(rfqFormParamsAtomFamily(cardId))
  const hedgeAmountInput = useNumberInput(params.hedge.amount)
  const hedgePriceInput = useNumberInput(params.hedge.price)
  const { form, getSubmissionRows } = useUnifiedOrderFormContext()
  // Not memoized: react-hook-form mutates `items` in place on setValue, so a useMemo
  // keyed on the array reference goes stale (same reasoning as order-form-summary.tsx,
  // which watches the same field the same way).
  const items = form.watch('items') ?? []
  // The maker picker must query against the SAME account the ticket will actually
  // submit under — not the card's own trading account, which can diverge from a
  // row's resolved account (e.g. a scoped row, or an inherited row on a different
  // exchange tab). Mirrors order-form-summary.tsx's own rfqAccountId derivation.
  const submissionAccountIds = getSubmissionRows().map(({ account }) => account.accountId)
  const rfqAccountId = resolveSingleRfqAccountId(submissionAccountIds)
  const accountId = rfqAccountId ?? ''
  const hasMixedAccounts = submissionAccountIds.length > 1 && !rfqAccountId
  const isAllSell = items.length > 0 && items.every((item) => item.side === 'sell')
  const selectedHedgeInstrument = useMemo(
    () =>
      instrumentListData?.instruments.find(
        (instrument) => instrument.symbol === params.hedge.symbol
      ),
    [instrumentListData?.instruments, params.hedge.symbol]
  )

  const update = (patch: Partial<RfqFormParams>) => setParams((prev) => ({ ...prev, ...patch }))

  // Params-level errors (maker floor, auto threshold/retry). Legs are validated
  // separately on submit, so an empty-leg placeholder is fine here.
  const errorByPath = useMemo(() => {
    const byPath: Record<string, string> = {}
    for (const e of validateRfqDraft(composeRfqDraft([], params), t)) {
      if (e.path && !byPath[e.path]) byPath[e.path] = e.message
    }
    return byPath
  }, [params, t])

  return (
    <div className="flex flex-col gap-4">
      <div data-tutorial="request" className="flex flex-col gap-4">
        <FormSection
          label={t('rfqMakersLabel')}
          error={hasMixedAccounts ? t('rfqSingleAccountOnly') : errorByPath['makerIds']}
        >
          <RfqMakerPicker
            accountId={accountId}
            selected={params.makerIds}
            onChange={(makerIds) => update({ makerIds })}
            disabled={hasMixedAccounts}
          />
        </FormSection>
        <FormToggleRow
          label={t('rfqDiscloseIdentityLabel')}
          checked={params.disclosed}
          onCheckedChange={(disclosed) => update({ disclosed })}
        />
      </div>

      <FormSection data-tutorial="execute" label={t('rfqExecutionLabel')}>
        <SegmentedControl<RfqExecutionMode>
          fill
          options={EXECUTION_MODES.map((mode) => ({
            value: mode,
            label: t(`rfqExecutionMode${mode === 'manual' ? 'Manual' : 'Auto'}`),
          }))}
          value={params.executionMode}
          onChange={(executionMode) => update({ executionMode })}
        />
      </FormSection>
      {params.executionMode === 'auto' && (
        <RfqAutoFields
          autoApprove={params.autoApprove}
          autoRetry={params.autoRetry}
          errorByPath={errorByPath}
          showErrors
          isAllSell={isAllSell}
          onApproveChange={(autoApprove) => update({ autoApprove })}
          onRetryChange={(autoRetry) => update({ autoRetry })}
        />
      )}

      {/* Optional fixed-price hedge */}
      <FormToggleRow
        data-tutorial="makers"
        label={t('rfqFixedPriceHedgeLegLabel')}
        info={t('rfqHedgeInfo')}
        checked={params.hedge.enabled}
        onCheckedChange={(enabled) => update({ hedge: { ...params.hedge, enabled } })}
      >
        <SegmentedControl<HedgeSide>
          fill
          options={[
            { value: 'buy', label: t('buy'), tone: 'buy' },
            { value: 'sell', label: t('sell'), tone: 'sell' },
          ]}
          value={params.hedge.side === OrderSide.ORDER_SIDE_SELL ? 'sell' : 'buy'}
          onChange={(side) =>
            update({
              hedge: {
                ...params.hedge,
                side: side === 'sell' ? OrderSide.ORDER_SIDE_SELL : OrderSide.ORDER_SIDE_BUY,
              },
            })
          }
        />
        <RfqInstrumentSelect
          value={params.hedge.symbol}
          displayValue={selectedHedgeInstrument?.exchangeSymbol || selectedHedgeInstrument?.symbol}
          exchange={primaryExchange}
          instruments={instrumentListData?.instruments ?? []}
          onChange={(instrument, lastPrice) =>
            update({
              hedge: {
                ...params.hedge,
                symbol: instrument.symbol,
                price: lastPrice ?? '',
              },
            })
          }
        />
        <div className="flex items-center gap-2">
          <FormInput
            containerClassName="flex-1"
            label={t('rfqHedgeAmountPlaceholder')}
            {...hedgeAmountInput.bind((amount) => update({ hedge: { ...params.hedge, amount } }))}
          />
          <FormInput
            containerClassName="flex-1"
            label={t('rfqHedgePriceAbsolutePlaceholder')}
            {...hedgePriceInput.bind((price) => update({ hedge: { ...params.hedge, price } }))}
          />
        </div>
      </FormToggleRow>
    </div>
  )
}
